import React, { useMemo, useCallback, useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Table, Tooltip, Flex, Space, ConfigProvider, Button, message } from 'antd';
import type { TableProps } from 'antd';

import { useAndStore, useStoreData } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { createCommand } from '../../base/actionParser';
import { parseNestedPath, setNestedValue } from '../../base/store/storePathUtils';
import { normalizeColumnDataIndex, resolveTableColumnKey } from '../../utils/normalizeColumnDataIndex';

// 拆分模块
import { applyColumnRendering } from './renderers';
import { ColumnSettingModal, applyColumnSettingValue, AND_TABLE_COLUMN_SETTING_MODAL_WIDTH } from './ColumnSettingModal';
import { getCoreConfig } from '../../config';
import { evaluateOperateVisible } from './operateVisibility';
import {
  applyColumnSort,
  buildSortFieldLookup,
  parseSorterToSortingFields,
  readSortingFieldsFromPagination,
  resolveSortingFieldsAfterChange,
  normalizeTableSortMode,
} from './columnSort';
import type {
  AndTableProps,
  ExtendedColumnType,
  OperateAction,
  OperateActionSetting,
  ColumnSettingItem,
  ColumnSettingValue,
  TableSortingField,
} from './types';
import './AndTable.css';

// 导出类型供外部使用
export type {
  AndTableProps,
  ExtendedColumnType,
  OperateAction,
  ColumnSettingItem,
  ColumnSettingValue,
  TableSortingField,
} from './types';

/** propsStore 合并时禁止覆盖的键：数据源/插槽/身份与 dataSet 绑定仍以设计器 props 为准 */
const PROPS_STORE_SKIP_KEYS = [
  'value',
  'validateStatus',
  'help',
  'dataSource',
  'children',
  'id',
  'sessionId',
  'develop',
  'dataSet',
  'dataSetField',
] as const;

/**
 * AndTable 组件
 * 封装 Ant Design Table，支持数据绑定和动作解析
 */
export const AndTable: React.FC<AndTableProps> = (incomingProps) => {
  const { id, sessionId } = incomingProps;

  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );

  const mergedProps = useMemo(
    () =>
      mergeStoreProps(
        { ...(incomingProps as Record<string, any>) },
        storeProps ?? undefined,
        { skipKeys: [...PROPS_STORE_SKIP_KEYS] }
      ) as AndTableProps,
    [incomingProps, storeProps]
  );

  const {
    develop,
    dataSet,
    dataSetField,
    columns = [],
    operateColumns = [],
    sampleData,

    operationColumnVisible = true,
    operationColumnSettings,
    selectRowEnable,
    singleSelectMode,
    selectAction,
    rowKeyField = 'id',
    displayRowIndex,
    queryAction,
    enableSort,
    sortMode,
    sortAction,
    operationColumnFixed,
    operationColumnWidth,
    paginationDataSet,
    pagination,
    emptyText,
    bordered,
    size,
    sticky,
    scroll,
    autoFillHeight,
    showTitle,
    showSummary,
    showFooter,
    renderTitle,
    renderSummary,
    renderFooter,
    children,
    token: propToken,
    columnSettingOpenKey: _columnSettingOpenKey,
    columnSettingsKey: _columnSettingsKey,
    persistColumnSettings,
    tableKey,
    ...restProps
  } = mergedProps;

  const persistColumnSettingsEnabled =
    persistColumnSettings === true || (persistColumnSettings as unknown) === 'true';

  const effectiveTableKey = useMemo(() => {
    const explicit = String(tableKey ?? '').trim();
    if (explicit) return explicit;
    return String(id ?? '').trim();
  }, [tableKey, id]);

  // Store hooks
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [localSortingFields, setLocalSortingFields] = useState<TableSortingField[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnSettingPosition, setColumnSettingPosition] = useState<{ x: number; y: number } | undefined>(undefined);

  // ==================== 工具函数 ====================

  const parseJson = useCallback(<T,>(value: T | string | undefined, fallback: T): T => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === 'string') {
      try {
        return (JSON.parse(value) as T) ?? fallback;
      } catch {
        return fallback;
      }
    }
    return value;
  }, []);

  // ==================== 数据源处理 ====================

  const columnsArray = useMemo(() => Array.isArray(columns) ? columns : [], [columns]);
  const operateColumnsArray = useMemo(() => Array.isArray(operateColumns) ? operateColumns : [], [operateColumns]);

  const paginationSlotName = useMemo(() => {
    const explicit = String(paginationDataSet ?? '').trim();
    if (explicit) return explicit;
    return dataSet ? `${dataSet}_pagination` : undefined;
  }, [paginationDataSet, dataSet]);

  // 本表用到的 store key 与列设置状态（一次算清）
  const keys = useMemo(
    () => ({
      dataSet: getDataSetStoreKey(sessionId, dataSet),
      pagination: paginationSlotName
        ? getDataSetStoreKey(sessionId, paginationSlotName)
        : undefined,
      selected: dataSet ? getDataSetStoreKey(sessionId, `${dataSet}_selected`) : undefined,
      columnSettingOpen: dataSet && sessionId
        ? getDataSetStoreKey(sessionId, `${dataSet}_columnSettingOpen`)
        : undefined,
      columnSettings: dataSet && sessionId
        ? getDataSetStoreKey(sessionId, `${dataSet}_columnSettings`)
        : undefined,
    }),
    [sessionId, dataSet, paginationSlotName]
  );

  const tableSortEnabled = enableSort === true || (enableSort as unknown) === 'true';
  const effectiveSortMode = normalizeTableSortMode(sortMode);

  const sortFieldLookup = useMemo(
    () => buildSortFieldLookup(columnsArray),
    [columnsArray],
  );

  const { style, className, ...tableProps } = restProps;

  const rawScroll = useMemo(() => {
    if (scroll) return scroll;

    let scrollX: any = (tableProps as any)['scroll-x'];
    let scrollY: any = (tableProps as any)['scroll-y'];

    if (String(scrollX) === 'true') scrollX = true;
    else if (String(scrollX) === 'false' || scrollX === '__unset__') scrollX = undefined;
    else if (scrollX === 'max-content') scrollX = 'max-content';

    if (scrollY === '' || scrollY === null || scrollY === undefined) scrollY = undefined;

    if (scrollX !== undefined || scrollY !== undefined) return { x: scrollX, y: scrollY };
    return undefined;
  }, [scroll, (tableProps as any)['scroll-x'], (tableProps as any)['scroll-y']]);

  // 列设置弹框：由 Extend 通过 updateItem(sessionId_dataSet_columnSettingOpen, true) 打开
  const columnSettingOpen = useAndStore((s) =>
    keys.columnSettingOpen ? s.dataSets[keys.columnSettingOpen]?.value === true : false
  );

  // 弹框打开时测量表头位置，将弹框放在表头下方且与表格右边缘对齐（关闭时不清空 position，避免关闭动画时先居中再消失）
  useLayoutEffect(() => {
    if (!columnSettingOpen || !containerRef.current) return;
    const header =
      containerRef.current.querySelector<HTMLElement>('.ant-table-thead') ||
      containerRef.current.querySelector<HTMLElement>('.ant-table-header');
    if (!header) return;
    const rect = header.getBoundingClientRect();
    setColumnSettingPosition({
      x: rect.right - AND_TABLE_COLUMN_SETTING_MODAL_WIDTH,
      y: rect.bottom + 8,
    });
  }, [columnSettingOpen]);
  const savedColumnSettings = useAndStore((s) => {
    const v = keys.columnSettings ? s.dataSets[keys.columnSettings]?.value : undefined;
    return typeof v === 'object' && v !== null ? (v as ColumnSettingValue) : undefined;
  });

  // 启用持久化时，mount 后从服务端 hydrate 列设置
  useEffect(() => {
    if (!persistColumnSettingsEnabled || !sessionId || !effectiveTableKey || !keys.columnSettings) {
      return;
    }
    const load = getCoreConfig().loadUserTableColumnSetting;
    if (!load) return;

    let cancelled = false;
    void (async () => {
      try {
        const value = await load({ sessionId, tableKey: effectiveTableKey });
        if (cancelled || !value) return;
        setDataSet(keys.columnSettings!, 'value', value, false);
      } catch (error) {
        console.error('[AndTable] load column settings failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    persistColumnSettingsEnabled,
    sessionId,
    effectiveTableKey,
    keys.columnSettings,
    setDataSet,
  ]);

  // 表格数据：从 store 按 dataSetKey + dataSetField 取数组（支持多级路径与 list[0]）
  const storeData = useStoreData(keys.dataSet, dataSetField);

  // 设计模式（develop=true）下强制用示例数据；运行时已绑定数据集时优先用 store 数据；未绑定时用示例数据
  const tableData = useMemo(() => {
    if (develop) return sampleData?.data || [];
    if (keys.dataSet) return storeData;
    return sampleData?.data || [];
  }, [develop, keys.dataSet, storeData, sampleData?.data]);

  // ==================== Token 配置 ====================

  const tableToken = useMemo(() => parseJson(propToken, {}), [propToken, parseJson]);
  const hasTableToken = tableToken && Object.keys(tableToken).length > 0;
  const tableThemeConfig = useMemo(
    () => (hasTableToken ? { components: { Table: tableToken } } : undefined),
    [hasTableToken, tableToken]
  );

  // ==================== 分页配置 ====================

  // 订阅 dataSets[keys.pagination]，使 store 更新 total/current 时触发重渲染
  const paginationChunk = useAndStore((s) =>
    keys.pagination ? s.dataSets[keys.pagination] : undefined
  );

  const storePagination = useMemo(() => {
    if (keys.pagination) return getDataSet(keys.pagination, '') || {};
    return {};
  }, [keys.pagination, getDataSet, paginationChunk]);

  const activeSortingFields = useMemo(() => {
    if (develop) return localSortingFields;
    return readSortingFieldsFromPagination(storePagination as Record<string, unknown>);
  }, [develop, localSortingFields, storePagination]);

  const hasPagination = useMemo(() => {
    if (pagination === false) return false;
    if (typeof pagination === 'object' && (pagination as any)?.visible === false) return false;
    return true;
  }, [pagination]);

  /** scroll-y=100%：外层 flex 容器纵向滚动，不向 antd 传 scroll.y（避免 fixHeader 分表导致横向滚不全） */
  const useContainerVerticalScroll = rawScroll?.y === '100%';

  const paginationConfig = useMemo(() => {
    if (!hasPagination) return false;

    const paginationProps = typeof pagination === 'object' ? pagination : {};
    const paginationRest = (restProps as Record<string, unknown>) || {};
    const showSizeChanger =
      paginationProps.showSizeChanger ??
      paginationRest['pagination-showSizeChanger'] ??
      true;
    const showQuickJumper =
      paginationProps.showQuickJumper ??
      paginationRest['pagination-showQuickJumper'] ??
      false;
    const simple = paginationProps.simple ?? paginationRest['pagination-simple'] ?? false;
    const defaultPageSize =
      paginationProps.defaultPageSize ?? paginationRest['pagination-defaultPageSize'] ?? 10;
    const pageSizeOptionsRaw =
      paginationProps.pageSizeOptions ?? paginationRest['pagination-pageSizeOptions'] ?? '10,20,50,100';
    const pageSizeOptions = typeof pageSizeOptionsRaw === 'string'
      ? pageSizeOptionsRaw.split(',').map((s) => parseInt(String(s).trim(), 10) || 10).filter((n) => !isNaN(n))
      : Array.isArray(pageSizeOptionsRaw)
        ? pageSizeOptionsRaw
        : [10, 20, 50, 100];

    return {
      ...storePagination,
      showSizeChanger,
      showQuickJumper,
      simple,
      defaultPageSize,
      pageSizeOptions,
      showTotal: (total: number, range: [number, number]) => {
        const template = (paginationProps as any).showTotal || '共 ${total} 条';
        return template
          .replace('${total}', String(total))
          .replace('${range[0]}', String(range[0]))
          .replace('${range[1]}', String(range[1]))
          .replace('${range}', `${range[0]}-${range[1]}`);
      },
      hideOnSinglePage: false,
    };
  }, [hasPagination, pagination, restProps, storePagination]);

  const handleTableChange = useCallback<NonNullable<TableProps['onChange']>>(
    (pag, _filters, sorter, extra) => {
      const isSortAction = extra?.action === 'sort';
      if (isSortAction && !tableSortEnabled) return;

      const prevSorting = develop
        ? localSortingFields
        : readSortingFieldsFromPagination(storePagination as Record<string, unknown>);

      const parsed = isSortAction
        ? resolveSortingFieldsAfterChange(
            prevSorting,
            sorter,
            sortFieldLookup,
            effectiveSortMode,
          )
        : parseSorterToSortingFields(sorter, sortFieldLookup);

      if (develop) {
        if (isSortAction) {
          setLocalSortingFields(parsed);
        }
        return;
      }

      if (!keys.pagination) return;

      const nextCurrent = isSortAction
        ? 1
        : (pag?.current ?? (storePagination as any).current ?? 1);
      const nextPageSize = pag?.pageSize ?? (storePagination as any).pageSize;

      const nextPagination: Record<string, unknown> = {
        ...storePagination,
        current: nextCurrent,
        pageSize: nextPageSize,
      };
      if (parsed.length > 0) {
        nextPagination.sortingFields = parsed;
      } else {
        delete nextPagination.sortingFields;
      }

      setDataSet(keys.pagination, '', nextPagination);

      const actionName = isSortAction
        ? (sortAction?.trim() || queryAction?.trim())
        : queryAction?.trim();
      if (actionName && sessionId) {
        addCommand(
          sessionId,
          createCommand(actionName, {
            sortingFields: parsed,
            pageNo: nextCurrent,
            pageSize: nextPageSize,
          }),
        );
      }
    },
    [
      develop,
      keys.pagination,
      sortFieldLookup,
      storePagination,
      setDataSet,
      queryAction,
      sortAction,
      sessionId,
      addCommand,
      tableSortEnabled,
      effectiveSortMode,
      localSortingFields,
    ],
  );

  // ==================== 列配置 ====================

  const handleActionClick = useCallback((action: OperateAction, record: any) => {
    if (sessionId) {
      addCommand(sessionId, createCommand(action.action, { ...record }));
    }
  }, [sessionId, addCommand]);

  /**
   * 解析 operationColumnSettings：
   * - 兼容字符串/对象/异常输入；默认 useCompact=false（保持 a 标签渲染）。
   * - 仅识别对象形态：actionSettings[key].{ styles, classNames }。
   * - legacy 兼容：仅当 actionSettings 为空时，将 buttonStyles[idx]: object 按 operateColumns[i].key 归一化为 styles；
   *   旧字段 buttonClassNames 不再迁移（行为变更，旧值会被静默忽略）。
   */
  const operationColumnSettingsParsed = useMemo(() => {
    let raw: any = operationColumnSettings;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) raw = undefined;
      else {
        try {
          raw = JSON.parse(trimmed);
        } catch {
          raw = undefined;
        }
      }
    }
    const useCompact = !!(raw && typeof raw === 'object' && (raw.useCompact === true || raw.useCompact === 'true'));
    const settings: Record<string, OperateActionSetting> = {};
    const rawSettings = raw && typeof raw === 'object' ? raw.actionSettings : undefined;
    if (rawSettings && typeof rawSettings === 'object') {
      Object.entries(rawSettings as Record<string, any>).forEach(([k, v]) => {
        if (!k || !v || typeof v !== 'object') return;
        const item: OperateActionSetting = {};
        if (v.styles && typeof v.styles === 'object' && !Array.isArray(v.styles)) {
          item.styles = v.styles;
        }
        if (v.classNames && typeof v.classNames === 'object' && !Array.isArray(v.classNames)) {
          item.classNames = v.classNames;
        }
        if (item.styles || item.classNames) settings[k] = item;
      });
    }
    if (Object.keys(settings).length === 0 && raw && typeof raw === 'object') {
      const legacyStyles = Array.isArray(raw.buttonStyles) ? (raw.buttonStyles as any[]) : null;
      if (legacyStyles) {
        operateColumnsArray.forEach((action, idx) => {
          const k = action?.key;
          if (!k) return;
          const styleItem = legacyStyles[idx];
          if (styleItem && typeof styleItem === 'object' && !Array.isArray(styleItem)) {
            settings[k] = { styles: styleItem };
          }
        });
      }
    }
    return { useCompact, actionSettings: settings };
  }, [operationColumnSettings, operateColumnsArray]);

  const runTableAction = useCallback(
    (actionName: string, arg: unknown) => {
      if (sessionId && actionName) {
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [sessionId, addCommand],
  );

  const handleCellValueChange = useCallback(
    (record: any, dataIndex: string, value: unknown) => {
      if (!keys.dataSet) return;
      const tableField = dataSetField && String(dataSetField).trim() !== '' ? dataSetField : '';
      const rowKeyValue = record?.[rowKeyField];
      const fieldPath = parseNestedPath(dataIndex);
      const nextTableData = tableData.map((row) => {
        const sameRowByKey =
          rowKeyValue !== undefined && rowKeyValue !== null
            ? row?.[rowKeyField] === rowKeyValue
            : row === record;
        if (!sameRowByKey) return row;
        if (fieldPath.length === 0) return row;
        return setNestedValue(row ?? {}, fieldPath, value);
      });
      setDataSet(keys.dataSet, tableField, nextTableData, false);
    },
    [keys.dataSet, dataSetField, rowKeyField, tableData, setDataSet]
  );

  // 默认列配置（用于弹窗重置、无持久化时的展示）
  const defaultColumnItems = useMemo<ColumnSettingItem[]>(() => {
    return columnsArray
      .map((col): ColumnSettingItem => {
        let fixed: ColumnSettingItem['fixed'] = false;
        if (col.fixed === true || col.fixed === 'left') fixed = 'left';
        else if (col.fixed === 'right') fixed = 'right';
        const w = (col as any).width;
        return {
          key: resolveTableColumnKey(col),
          title: typeof col.title === 'string' ? col.title : '列',
          visible: col.visible === true,
          fixed,
          width: typeof w === 'number' ? w : 'auto',
        };
      })
      .filter((i) => i.key);
  }, [columnsArray]);

  const defaultRowIndexVisible = displayRowIndex === true;
  const defaultScrollX = false;
  const defaultSize = (size === 'small' || size === 'large' ? size : 'middle') as 'small' | 'middle' | 'large';

  // 当前列配置（来自 store 或默认）
  const { rowIndexVisible: effectiveRowIndexVisible, items: effectiveColumnItems, scrollX: scrollXPersisted, size: effectiveSize } = useMemo(() => {
    return applyColumnSettingValue(
      savedColumnSettings,
      defaultColumnItems,
      defaultRowIndexVisible,
      defaultSize
    );
  }, [savedColumnSettings, defaultColumnItems, defaultRowIndexVisible, defaultSize]);

  // 是否有列自定义（有 openKey 或 settingsKey 即启用）
  const hasColumnSetting = Boolean(dataSet && sessionId);

  /** 列设置弹窗「自动滚动」勾选状态：store 有值用 store，否则与表格实际横向 scroll 回退一致 */
  const scrollXForModal = useMemo(() => {
    if (scrollXPersisted !== undefined) return scrollXPersisted;
    const rx = rawScroll?.x;
    if (rx === true || (typeof rx === 'number' && Number.isFinite(rx) && rx > 0)) return true;
    if (rx === 'max-content') return false;
    return defaultScrollX;
  }, [scrollXPersisted, rawScroll?.x, defaultScrollX]);

  /** 传给 Table 的 scroll.x：store 明确写了用 store；否则用 props scroll-x；最后用 defaultScrollX */
  const resolvedTableScrollX = useMemo(() => {
    if (!hasColumnSetting) return rawScroll?.x;
    if (scrollXPersisted !== undefined) return scrollXPersisted ? true : 'max-content';
    if (rawScroll?.x !== undefined) return rawScroll.x;
    return defaultScrollX ? true : 'max-content';
  }, [hasColumnSetting, scrollXPersisted, rawScroll?.x, defaultScrollX]);

  const tableColumns = useMemo(() => {
    const result: ExtendedColumnType[] = [];

    // 序号列（受列设置控制）
    if (effectiveRowIndexVisible) {
      result.push(
        applyColumnRendering({
          title: '序号',
          key: 'rowIndex',
          width: 64,
          align: 'center',
          ellipsis: true,
          render: (_: any, __: any, index: number) => {
            const current = storePagination.current || 1;
            const pageSize = storePagination.pageSize || 10;
            return (current - 1) * pageSize + index + 1;
          },
        }),
      );
    }

    // 数据列（顺序、显隐、固定受列设置控制）
    const keyToCol = new Map(
      columnsArray.map((col) => [resolveTableColumnKey(col), col]),
    );
    const visibleItems = effectiveColumnItems.filter((i) => i.visible);
    visibleItems.forEach((item) => {
      const col = keyToCol.get(item.key);
      if (!col) return;
      const fixed = item.fixed || undefined;
      const colWidth = item.width === 'auto' || item.width === undefined ? undefined : item.width;
      const withSort = applyColumnSort(
        {
          ...col,
          dataIndex:
            normalizeColumnDataIndex(col.dataIndex ?? col.key) ?? col.dataIndex ?? col.key,
          fixed: fixed || col.fixed,
          width: colWidth ?? (col as any).width,
        },
        activeSortingFields,
        { sortMode: effectiveSortMode, tableSortEnabled },
      );
      result.push(
        applyColumnRendering(withSort, {
          runAction: runTableAction,
          onCellValueChange: handleCellValueChange,
        }),
      );
    });

    // 操作列
    if (operationColumnVisible !== false && operateColumnsArray.length > 0) {
      const { useCompact, actionSettings } = operationColumnSettingsParsed;
      const opColumnFixed =
        operationColumnFixed === true || (operationColumnFixed as unknown) === 'true';
      const getSetting = (action: OperateAction): OperateActionSetting | undefined => {
        const k = action?.key;
        return k ? actionSettings[k] : undefined;
      };
      const defaultFixedActionColumnWidth = Math.min(
        Math.max(operateColumnsArray.length * 56 + 32, 100),
        280,
      );
      const configuredActionColumnWidth = (() => {
        const raw = operationColumnWidth;
        if (raw == null || raw === '') return undefined;
        const n = typeof raw === 'number' ? raw : Number(raw);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      })();
      const actionColumnWidth =
        configuredActionColumnWidth != null
          ? configuredActionColumnWidth
          : opColumnFixed
            ? defaultFixedActionColumnWidth
            : undefined;
      result.push({
        title: '操作',
        key: 'action',
        fixed: opColumnFixed ? 'right' : undefined,
        width: actionColumnWidth,
        render: (_: any, record: any) => {
          const visibleActions = operateColumnsArray
            .map((action, idx) => ({ action, idx }))
            .filter(({ action }) => evaluateOperateVisible(record, action.visibleExpression));
          if (visibleActions.length === 0) return null;
          return useCompact ? (
            <Space.Compact>
              {visibleActions.map(({ action, idx }) => {
                const setting = getSetting(action);
                return (
                  <Tooltip key={action.key ?? idx} title={action.tooltip}>
                    <Button
                      size="small"
                      onClick={() => handleActionClick(action, record)}
                      styles={setting?.styles}
                      classNames={setting?.classNames}
                    >
                      {action.title}
                    </Button>
                  </Tooltip>
                );
              })}
            </Space.Compact>
          ) : (
            <Space>
              {visibleActions.map(({ action, idx }) => (
                <Tooltip key={action.key ?? idx} title={action.tooltip}>
                  <a onClick={() => handleActionClick(action, record)} style={{ marginRight: 5 }}>
                    {action.title}
                  </a>
                </Tooltip>
              ))}
            </Space>
          );
        },
      });
    }

    return result;
  }, [
    columnsArray,
    operateColumnsArray,
    effectiveRowIndexVisible,
    effectiveColumnItems,
    operationColumnVisible,
    operationColumnSettingsParsed,
    operationColumnFixed,
    operationColumnWidth,
    storePagination,
    activeSortingFields,
    effectiveSortMode,
    tableSortEnabled,
    handleActionClick,
    runTableAction,
    handleCellValueChange,
  ]);

  const handleColumnSettingClose = useCallback(() => {
    if (keys.columnSettingOpen) setDataSet(keys.columnSettingOpen, '', false);
  }, [keys.columnSettingOpen, setDataSet]);

  const handleColumnSettingSave = useCallback(
    (value: ColumnSettingValue) => {
      // 写入 value 字段，与读取 s.dataSets[keys.columnSettings]?.value 一致
      if (keys.columnSettings) setDataSet(keys.columnSettings, 'value', value);
    },
    [keys.columnSettings, setDataSet]
  );

  const handleColumnSettingPersist = useCallback(
    async (value: ColumnSettingValue) => {
      const save = getCoreConfig().saveUserTableColumnSetting;
      if (!save) {
        message.error('未配置列设置保存接口');
        return;
      }
      if (!sessionId || !effectiveTableKey) {
        message.error('缺少 sessionId 或 tableKey');
        return;
      }
      await save({ sessionId, tableKey: effectiveTableKey, tableJson: value });
      message.success('列设置已保存');
    },
    [sessionId, effectiveTableKey]
  );

  // ==================== 行选择 ====================

  const rowSelection = useMemo(() => {
    // 单选模式下不使用 rowSelection（避免空白列）
    if (singleSelectMode) return undefined;
    if (!selectRowEnable) return undefined;

    return {
      selectedRowKeys,
      onChange: (rowKeys: React.Key[], selectedRows: any[], info: { type?: string }) => {
        setSelectedRowKeys(rowKeys);
        if (keys.selected) setDataSet(keys.selected, '', rowKeys);
        if (selectAction?.trim() && sessionId) {
          addCommand(sessionId, createCommand(selectAction.trim(), {
            selectedRowKeys: rowKeys,
            selectedRows,
            info,
          }));
        }
      },
    };
  }, [selectRowEnable, singleSelectMode, selectedRowKeys, keys.selected, setDataSet, selectAction, sessionId, addCommand]);

  // 单选模式：点击行选中
  const handleRowClick = useCallback((record: any) => {
    if (!singleSelectMode) return;

    const key = record[rowKeyField];
    const newSelectedKeys = [key];

    setSelectedRowKeys(newSelectedKeys);
    if (keys.selected) setDataSet(keys.selected, '', newSelectedKeys);
    if (selectAction?.trim() && sessionId) {
      addCommand(sessionId, createCommand(selectAction.trim(), { record, selectedRowKeys: newSelectedKeys }));
    }
  }, [singleSelectMode, selectAction, sessionId, rowKeyField, keys.selected, setDataSet, addCommand]);

  const rowClassName = useCallback((record: any) => {
    if (!singleSelectMode) return '';
    return selectedRowKeys.includes(record[rowKeyField]) ? 'ant-table-row-selected' : '';
  }, [singleSelectMode, rowKeyField, selectedRowKeys]);

  const onRow = useCallback((record: any) => {
    if (!singleSelectMode) return {};
    return { onClick: () => handleRowClick(record), style: { cursor: 'pointer' } };
  }, [singleSelectMode, handleRowClick]);

  // ==================== Scroll 配置 ====================

  const finalScroll = useMemo(() => {
    const scrollX = resolvedTableScrollX;

    if (useContainerVerticalScroll) {
      if (scrollX !== undefined) return { x: scrollX };
      return undefined;
    }

    if (!rawScroll && !hasColumnSetting) return undefined;
    const scrollY = rawScroll?.y;
    if (scrollX !== undefined || scrollY !== undefined) return { x: scrollX, y: scrollY };
    return rawScroll;
  }, [rawScroll, useContainerVerticalScroll, hasColumnSetting, resolvedTableScrollX]);

  const tableScrollContainerStyle = useMemo((): React.CSSProperties => {
    const base: React.CSSProperties = {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    };
    if (useContainerVerticalScroll) {
      base.overflow = 'auto';
    }
    return base;
  }, [useContainerVerticalScroll]);

  // ==================== 插槽处理 ====================

  const { titleSlot, summarySlot, footerSlot, otherChildren } = useMemo(() => {
    let titleSlot: React.ReactNode = null;
    let summarySlot: React.ReactNode = null;
    let footerSlot: React.ReactNode = null;
    const otherChildren: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        otherChildren.push(child);
        return;
      }

      const slotType = (child.props as any)['data-slot-type'];
      if (slotType === 'title') titleSlot = child;
      else if (slotType === 'summary') summarySlot = child;
      else if (slotType === 'footer') footerSlot = child;
      else otherChildren.push(child);
    });

    return { titleSlot, summarySlot, footerSlot, otherChildren };
  }, [children]);

  const finalTitle = useMemo(() => {
    if (titleSlot) return undefined;
    if (renderTitle) return renderTitle;
    if (showTitle && develop) return () => <div data-table-slot-container="title" style={{ minHeight: 20 }} />;
    return undefined;
  }, [showTitle, titleSlot, renderTitle, develop]);

  const finalSummary = useMemo(() => {
    if (renderSummary) return renderSummary;
    if (showSummary && summarySlot) {
      return () => (
        <tr><td colSpan={tableColumns.length} style={{ padding: 0 }}>{summarySlot}</td></tr>
      );
    }
    if (showSummary && develop) {
      return () => (
        <tr><td colSpan={tableColumns.length} style={{ padding: 0 }}>
          <div data-table-slot-container="summary" style={{ minHeight: 20 }} />
        </td></tr>
      );
    }
    return undefined;
  }, [showSummary, summarySlot, renderSummary, tableColumns.length, develop]);

  // ==================== 渲染 ====================

  const tableSize = hasColumnSetting ? effectiveSize : size;

  /** max-content 时 rc-table 默认 tableLayout=auto，header/body 实测列宽易漂移；显式 fixed 使 column.width 驱动 colgroup */
  const tableLayout = finalScroll?.x !== undefined ? ('fixed' as const) : undefined;

  const tableElement = (
    <Table
      {...tableProps}
      columns={tableColumns}
      dataSource={tableData}
      rowKey={rowKeyField}
      rowSelection={rowSelection}
      rowClassName={rowClassName}
      pagination={paginationConfig}
      locale={{ emptyText }}
      bordered={bordered}
      size={tableSize}
      sticky={sticky}
      scroll={finalScroll}
      tableLayout={tableLayout}
      title={finalTitle}
      summary={finalSummary}
      footer={undefined}
      onRow={onRow}
      onChange={handleTableChange}
      {...(tableSortEnabled
        ? { showSorterTooltip: { target: 'sorter-icon' as const } }
        : {})}
    />
  );

  const rootClassName = ['and-table', className].filter(Boolean).join(' ');

  return (
    <Flex
      ref={containerRef}
      vertical
      style={{ width: '100%', height: '100%', ...style }}
      className={rootClassName}
    >
      {/* Title 插槽 */}
      {titleSlot && <div data-table-slot="title">{titleSlot}</div>}

      {/* 表格容器：scroll-y=100% 时由此处 overflow 承担纵向滚动 */}
      <div style={tableScrollContainerStyle}>
        {singleSelectMode && (() => {
          const bg = tableToken?.rowSelectedBg || '#e6f4ff';
          const hoverBg = tableToken?.rowSelectedHoverBg || tableToken?.rowSelectedBg || '#bae0ff';
          const textColor = tableToken?.rowSelectedColor;
          const linkColor = tableToken?.rowSelectedLinkColor ?? textColor;
          const linkHoverColor = tableToken?.rowSelectedLinkHoverColor ?? linkColor;
          return (
            <style>{`
              .ant-table-row-selected > td {
                background: ${bg} !important;
                ${textColor ? `color: ${textColor};` : ''}
              }
              .ant-table-row-selected:hover > td {
                background: ${hoverBg} !important;
              }
              ${linkColor ? `.ant-table-row-selected > td a { color: ${linkColor}; }` : ''}
              ${linkHoverColor ? `.ant-table-row-selected > td a:hover { color: ${linkHoverColor}; }` : ''}
            `}</style>
          );
        })()}
        {tableThemeConfig ? (
          <ConfigProvider theme={tableThemeConfig}>{tableElement}</ConfigProvider>
        ) : (
          tableElement
        )}
      </div>

      {/* Summary 插槽 */}
      {summarySlot}

      {/* Footer 插槽 */}
      {footerSlot && <div data-table-slot="footer">{footerSlot}</div>}

      {/* 其他子元素 */}
      {otherChildren}

      {/* 列设置弹窗：store 键为 ${dataSet}_columnSettingOpen / ${dataSet}_columnSettings */}
      {hasColumnSetting && (
        <ColumnSettingModal
          open={columnSettingOpen}
          position={columnSettingPosition}
          rowIndexVisible={effectiveRowIndexVisible}
          columnItems={effectiveColumnItems}
          defaultRowIndexVisible={defaultRowIndexVisible}
          defaultColumnItems={defaultColumnItems}
          scrollX={scrollXForModal}
          defaultScrollX={defaultScrollX}
          size={effectiveSize}
          defaultSize={defaultSize}
          onSave={handleColumnSettingSave}
          onClose={handleColumnSettingClose}
          persistEnabled={persistColumnSettingsEnabled}
          onPersist={persistColumnSettingsEnabled ? handleColumnSettingPersist : undefined}
        />
      )}
    </Flex>
  );
};

export default AndTable;
